/**
 * web/src/screens/Notebook/giftflow/index.tsx — GiftFlow, the four-step gift
 * flow that replaces the old pill strip on the Notebook Gift tab (card
 * NYJEJRCBfBGQ). Orchestration only: it owns the header, the four-segment
 * stepper with its `Step N of 4 · <Label>` caption and Back affordance, and
 * switches between the home screen and the four steps. All state lives in
 * useGiftFlow (hooks.ts); each screen is its own module.
 */
import { GiftHome } from './home';
import { StepOccasion } from './step1';
import { StepQuestions } from './step2';
import { StepIdeas } from './step3';
import { StepPlan } from './step4';
import { useGiftFlow, type GiftFlowState } from './hooks';
import { STEP_LABELS, stepNoOf } from './types';
import './gift-flow.css';

export type { GiftFlowState };

export function GiftFlow({ onOpenUs }: { onOpenUs?: () => void }) {
  const g = useGiftFlow({ onOpenUs });
  const step = stepNoOf(g.view);

  return (
    <div className="nb-card gift-flow" data-testid="gift-flow">
      <div className="gf-head">
        <h3 className="gf-title" data-testid="gift-flow-title">Gifts</h3>
      </div>
      {g.err && <p className="gf-err" data-testid="gift-flow-err">{g.err}</p>}

      {step !== null && (
        <div className="gf-stepper" data-testid="gift-flow-stepper">
          <ol className="gf-segments" aria-label="Gift steps">
            {STEP_LABELS.map((label, i) => {
              const n = (i + 1) as 1 | 2 | 3 | 4;
              const isCur = step === n;
              // Steps past the occasion need a working search; without one
              // only the first segment is reachable.
              const enabled = n === 1 || g.activeId !== null;
              return (
                <li key={label}>
                  <button
                    type="button"
                    className={`gf-segment${isCur ? ' is-on' : ''}`}
                    data-testid={`gift-flow-seg-${n}`}
                    aria-current={isCur ? 'step' : undefined}
                    disabled={!enabled}
                    onClick={() => g.setView(n)}
                  >
                    {label}
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="gf-stepcap">
            <button
              type="button"
              className="pear-btn pear-btn-quiet gf-back"
              data-testid="gift-flow-back"
              onClick={g.back}
            >
              Back
            </button>
            <span className="gf-caption" data-testid="gift-flow-step-caption">
              Step {step} of 4 · {STEP_LABELS[step - 1]}
            </span>
          </div>
        </div>
      )}

      {g.loading && g.view === 'home' && <p className="gf-muted" data-testid="gift-flow-loading">Loading…</p>}

      {g.view === 'home' && <GiftHome g={g} />}
      {g.view === 1 && <StepOccasion g={g} />}
      {g.view === 2 && <StepQuestions g={g} defaultsMode={false} />}
      {g.view === 'defaults' && <StepQuestions g={g} defaultsMode />}
      {g.view === 3 && <StepIdeas g={g} />}
      {g.view === 4 && <StepPlan g={g} />}
    </div>
  );
}
